import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import type { SpecValueKind } from '@inference.engine/ad-formats/web';
import { decodeDraft, draftFromValue, emptyDraft, type DraftError } from '../lib/spec-draft';
import type { LibrarySpecValue, SpecValueInput } from '../model/types';
import { ProvenanceFields } from './ProvenanceFields';
import { emptyProvenance, type ProvenanceDraft } from './provenance';
import { SpecDiagram } from './SpecDiagram';
import { SpecValueFields } from './SpecValueFields';

interface SpecValueSheetProps {
  open: boolean;
  property: string;
  kind: SpecValueKind;
  current: LibrarySpecValue | undefined;
  canvas: { w: number; h: number };
  isSaving: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (input: SpecValueInput | null) => void;
}

export function SpecValueSheet({
  open,
  property,
  kind,
  current,
  canvas,
  isSaving,
  onOpenChange,
  onSave,
}: SpecValueSheetProps) {
  const { t } = useTranslation('admin');
  const [draft, setDraft] = useState(emptyDraft);
  const [notApplicable, setNotApplicable] = useState(false);
  const [provenance, setProvenance] = useState<ProvenanceDraft>(emptyProvenance);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    setNotApplicable(current?.value === null);
    setDraft(current ? draftFromValue(kind, property, current.value) : emptyDraft());
    setProvenance(
      current
        ? {
            requirement: current.requirement,
            scope: current.scope,
            notes: current.notes ?? '',
          }
        : emptyProvenance(),
    );
  }, [open, current, kind, property]);

  const decoded = notApplicable ? { value: null } : decodeDraft(kind, property, draft);
  const valueError = 'error' in decoded ? draftErrorMessage(t, decoded.error) : undefined;

  const save = () => {
    setSubmitted(true);
    if ('error' in decoded) return;
    onSave({
      value: decoded.value,
      requirement: provenance.requirement,
      scope: provenance.scope,
      notes: provenance.notes.trim() || null,
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-md overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{t('admin.adFormats.spec.edit', { property })}</SheetTitle>
          <SheetDescription>{t('admin.adFormats.spec.sheetDescription')}</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-md px-md">
          <SpecDiagram
            kind={kind}
            property={property}
            value={'value' in decoded ? decoded.value : null}
            canvas={canvas}
          />
          <div className="flex items-center gap-xs">
            <Checkbox
              id="spec-na"
              checked={notApplicable}
              onCheckedChange={(checked) => setNotApplicable(checked === true)}
            />
            <Label htmlFor="spec-na">{t('admin.adFormats.spec.notApplicableLabel')}</Label>
          </div>
          {!notApplicable && (
            <SpecValueFields
              kind={kind}
              property={property}
              draft={draft}
              error={submitted ? valueError : undefined}
              onChange={setDraft}
            />
          )}
          <ProvenanceFields
            // eslint-disable-next-line i18next/no-literal-string -- element id prefix
            idPrefix="spec"
            draft={provenance}
            onChange={setProvenance}
          />
        </div>
        <SheetFooter className="flex-row justify-between">
          {current ? (
            <Button variant="ghost" disabled={isSaving} onClick={() => onSave(null)}>
              {t('admin.adFormats.spec.clear')}
            </Button>
          ) : (
            <span />
          )}
          <Button disabled={isSaving} onClick={save}>
            {isSaving ? t('admin.adFormats.saving') : t('admin.adFormats.save')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function draftErrorMessage(
  t: ReturnType<typeof useTranslation<'admin'>>['t'],
  code: DraftError,
): string {
  switch (code) {
    case 'value_required':
      return t('admin.adFormats.spec.errors.valueRequired');
    case 'size_side':
      return t('admin.adFormats.spec.errors.sizeSide');
    case 'size_required':
      return t('admin.adFormats.spec.errors.sizeRequired');
    case 'margin_side':
      return t('admin.adFormats.spec.errors.marginSide');
    case 'kb_format':
      return t('admin.adFormats.spec.errors.kbFormat');
    case 'list_required':
      return t('admin.adFormats.spec.errors.listRequired');
    case 'size_format':
      return t('admin.adFormats.spec.errors.size_format');
    case 'number_format':
      return t('admin.adFormats.spec.errors.number_format');
    case 'range_format':
      return t('admin.adFormats.spec.errors.range_format');
    case 'number_list_format':
      return t('admin.adFormats.spec.errors.number_list_format');
    case 'boolean_format':
      return t('admin.adFormats.spec.errors.boolean_format');
    case 'margins_format':
      return t('admin.adFormats.spec.errors.margins_format');
  }
}
